import { Invite } from "../../types";
import { AppDispatch } from "../hooks";
import { DateString, HoldingPermissionsApi } from "../../api";
import { useMemo } from "react";
import { accessLevelNames } from "./accessLevels";
import { showOkCancel } from "../../../../../../shared/react/msgbox";
import { isArrayEmpty } from "../../../../../../shared/utils/array";
import { classes } from "../../../../../../shared/react/react";


interface Props {
    invites: Invite[]
    api: HoldingPermissionsApi
    dispatch: AppDispatch
}

export function InvitesList(props: Props) {

    const { api, dispatch } = props

    const invites = useInvites(props.invites)

    if (isArrayEmpty(invites)) {
        return <p>
            No invitations to join this holding have been sent yet. You can use the form below to send one.
        </p>
    }

    const sendAgain = async (i: Invite) => {

        const ok = await showOkCancel(
            <>
                <p>
                    This will send <b>{i.email}</b> a new invitation to join your holding as a member.
                </p>
            </>,
            "Send the invitation again"
        )

        if (!ok) return

        const invites = await api.sendNewInvite({ email: i.email, accessLevel: i.accessLevel })

        if (invites) {
            dispatch({ type: "load", payload: { invites } })
        }


    }

    const cancel = async (i: Invite) => {

        const ok = await showOkCancel(
            <>
                <p>
                    This will cancel the invitation sent to <b>{i.email}</b>.
                </p>
            </>,
            "Cancel the invitation"
        )

        if (!ok) return

        const invites = await api.cancelInvite(i.email)

        if (invites) {
            dispatch({ type: "load", payload: { invites } })
        }


    }

    return <div className="list">

        {
            invites.map(i => <div key={i.email} className={classes("invite", i.acceptedOn ? "accepted" : "pending")}>

                <div style={{ gridColumn: 1 }}>
                    {i.email}
                </div>

                <div style={{ gridColumn: 2 }}>
                    {accessLevelNames[i.accessLevel] ?? i.accessLevel}
                </div>

                <div style={{ gridColumn: 3 }}>
                    {new Date(i.issuedOn).toLocaleDateString()}
                </div>

                <div style={{ gridColumn: 4 }} className="status">
                    {
                        i.acceptedOn
                            ? <i className="fa-solid fa-circle-check text-success" />
                            : <i className="fa-regular fa-hourglass-half" />
                    }
                    {
                        i.acceptedOn
                            ? <>
                                Accepted
                                (<DateInfo date={i.acceptedOn} />)
                            </>
                            : "Pending"
                    }
                </div>

                {
                    (!i.acceptedOn) &&
                    <div className="flex-row actions" style={{ gridColumn: 5 }}>
                        <button type="button" className="btn btn-link" onClick={() => sendAgain(i)}>
                            <i className="fa-regular fa-paper-plane"></i>
                            &nbsp;Send again
                        </button>
                        <button type="button" className="btn btn-link" onClick={() => cancel(i)}>
                            <i className="fa-solid fa-ban"></i>
                            &nbsp;Cancel
                        </button>
                    </div>
                }

            </div>)
        }

    </div>

}

function useInvites(invites: Invite[]) {

    return useMemo(() => {

        return [...(invites ?? [])]
            .sort((a, b) => {
                let d = (a.acceptedOn ? 1 : 0) - (b.acceptedOn ? 1 : 0)
                d ||= b.issuedOn.localeCompare(a.issuedOn)
                return d
            })


    }, [invites])

}

function DateInfo({ date }: { date: DateString }) {

    if (!date) {
        return null
    }

    const d = new Date(date)

    return <span title={d.toLocaleTimeString()}>
        {d.toLocaleDateString()}
    </span>

}